import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { apiCall } from 'modules/core'
import {
  AllBusinessesResponse,
  BusinessEntity,
  BusinessesService,
  BusinessMembershipEntity,
  CreateBusinessDto,
  SigninUserResponseDto,
  UpdateBusinessDto,
  VerifyOtpResponseDto,
} from 'openapi'
import { useBrandKitStore } from 'modules/creative/store/brand-kit-store'
import { RootState } from 'types'
import {
  logout,
  providerSigninAction,
  selectBusinessSetupAction,
  verifyOtpAction,
} from '../../authentication/slices/authentication-view.slice'
import { selectBusinessId } from '../../common/selectors/authentication-view.selector'

const {
  businessControllerCreateV1,
  businessControllerUpdateV1,
  businessControllerFindMyBusinessesV1,
  businessControllerDeleteV1,
  businessControllerLeaveV1,
  businessControllerCompleteSetupV1,
} = BusinessesService

/**
 * Clears every per-business client state slot before a switch / delete / leave.
 * Anything keyed to the OLD businessId (brand kit, persisted Redux entities
 * scoped to the previous business) must be reset here so the post-reload
 * hydration starts from a clean slate. The hard `window.location.reload()`
 * below catches anything we missed, but doing it here too prevents the brief
 * flash of stale brand kit between "Redux updated" and "page reloads".
 */
function clearPerBusinessState(): void {
  try {
    useBrandKitStore.getState().resetBrandKit()
  } catch (err) {
    console.warn('Failed to reset brand kit before business switch:', err)
  }
}

/**
 * Fetch businesses for current user (used on app init for existing sessions)
 */
export const findMyBusinesses = createAsyncThunk(
  'businesses/findMy',
  async (_, { rejectWithValue }) =>
    await apiCall(businessControllerFindMyBusinessesV1, rejectWithValue)
)

/**
 * Create a new business
 */
export const createBusinessAction = createAsyncThunk(
  'businesses/create',
  async (dto: CreateBusinessDto, { rejectWithValue }) =>
    await apiCall(businessControllerCreateV1, rejectWithValue, dto)
)

/**
 * Update the current business
 */
export const updateBusinessAction = createAsyncThunk(
  'businesses/update',
  async (dto: UpdateBusinessDto, { rejectWithValue }) =>
    await apiCall(businessControllerUpdateV1, rejectWithValue, dto)
)

/**
 * Switch the active business: mint new tokens, wipe per-business client state,
 * and let the auth slice apply the token via its `selectBusinessSetupAction`
 * reducer. The caller is responsible for the hard reload (kept in the UI so
 * the thunk stays testable and so that batched switch+log flows can choose
 * when to reload).
 */
export const switchBusinessAction = createAsyncThunk(
  'businesses/switch',
  async ({ businessId }: { businessId: string }, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(selectBusinessSetupAction({ businessId })).unwrap()
      clearPerBusinessState()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to switch business'
      return rejectWithValue({ message })
    }
  }
)

/**
 * The generated client's ApiError.message is a raw status dump ("Generic
 * Error: status: 409; …"); the actionable text the server sent lives in
 * body.message, and structured 409s (the billing-gate
 * cancel-your-subscription-first refusal) also carry body.reason. This is the
 * single place that knows the body shape — prefer it, fall back safely if the
 * shape ever changes.
 */
const extractApiError = (
  error: unknown,
  fallback: string
): { message: string; reason?: string } => {
  const body = (error as { body?: { message?: unknown; reason?: unknown } })?.body
  return {
    message: typeof body?.message === 'string' && body.message ? body.message : fallback,
    reason: typeof body?.reason === 'string' ? body.reason : undefined,
  }
}

/**
 * Delete the current business (owner-only on the server). The server returns
 * a fresh token pair scoped to the next-best business (or PENDING_BUSINESS_ID
 * if the user has none left) — the auth slice's matcher reducer applies it.
 * UI caller is responsible for the reload.
 */
export const deleteCurrentBusinessAction = createAsyncThunk(
  'businesses/deleteCurrent',
  async (_: void, { rejectWithValue }) => {
    try {
      const tokens: SigninUserResponseDto = await businessControllerDeleteV1()
      clearPerBusinessState()
      return tokens
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'Failed to delete business'))
    }
  }
)

/**
 * Flip `setupCompletedAt` on a draft business so it becomes visible in
 * list/auto-selection. Idempotent on the server, so it's safe to call from
 * any paywall completion (first-time onboarding included). Returns the
 * updated business so the entity adapter stays in sync.
 */
export const markBusinessSetupCompleteAction = createAsyncThunk(
  'businesses/markSetupComplete',
  async ({ businessId }: { businessId: string }, { rejectWithValue }) =>
    await apiCall(businessControllerCompleteSetupV1, rejectWithValue, businessId)
)

/**
 * Leave the current business (non-owner only on the server). Same response
 * shape as delete: tokens for the next-best business.
 */
export const leaveCurrentBusinessAction = createAsyncThunk(
  'businesses/leaveCurrent',
  async (_: void, { rejectWithValue }) => {
    try {
      const tokens: SigninUserResponseDto = await businessControllerLeaveV1()
      clearPerBusinessState()
      return tokens
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'Failed to leave business'))
    }
  }
)

const entityAdapter = createEntityAdapter<BusinessEntity>({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
})

const initialState = entityAdapter.getInitialState()

const slice = createSlice({
  name: 'businessEntities',
  initialState,
  reducers: {
    /**
     * Set businesses from login response (business memberships contain business data)
     */
    setBusinessesFromMemberships: (state, action: PayloadAction<BusinessMembershipEntity[]>) => {
      const businesses = action.payload
        .map(membership => membership.business)
        .filter((business): business is BusinessEntity => !!business)

      entityAdapter.setAll(state, businesses)
    },
  },
  extraReducers: builder => {
    builder.addCase(logout, () => initialState)

    // Add fetched businesses to the store (for existing sessions)
    builder.addCase(
      findMyBusinesses.fulfilled,
      (state, action: PayloadAction<AllBusinessesResponse>) => {
        entityAdapter.setAll(state, action.payload.data)
      }
    )

    // Add created business to the store
    builder.addCase(createBusinessAction.fulfilled, (state, action) => {
      entityAdapter.upsertOne(state, action.payload)
    })

    // Update existing business in the store
    builder.addCase(updateBusinessAction.fulfilled, (state, action) => {
      entityAdapter.upsertOne(state, action.payload)
    })

    // Reveal the newly-completed draft (or refresh the completed timestamp)
    builder.addCase(markBusinessSetupCompleteAction.fulfilled, (state, action) => {
      entityAdapter.upsertOne(state, action.payload)
    })

    // No reducer for deleteCurrentBusinessAction / leaveCurrentBusinessAction:
    // the reload that follows wipes the adapter and findMyBusinesses refills
    // it. An incremental reducer would need the old businessId, which the
    // server response doesn't carry.

    // Store businesses from login/signup response
    builder.addMatcher(
      action =>
        action.type === verifyOtpAction.fulfilled.type ||
        action.type === providerSigninAction.fulfilled.type,
      (state, action: PayloadAction<VerifyOtpResponseDto>) => {
        const { businesses } = action.payload
        if (businesses?.length) {
          const businessEntities = businesses
            .map(membership => membership.business)
            .filter((business): business is BusinessEntity => !!business)

          entityAdapter.setAll(state, businessEntities)
        }
      }
    )
  },
})

export const businessEntitiesReducer = slice.reducer

export const { setBusinessesFromMemberships } = slice.actions

// Selectors
const { selectAll, selectById, selectEntities } = entityAdapter.getSelectors(
  (state: RootState) => state.businessEntities
)

/**
 * Businesses for any UI surface that lists "real" businesses (switcher,
 * NewBusinessSetupPage's "other businesses" probe). Drafts (no
 * `setupCompletedAt`) are filtered out so a half-built draft from
 * `createWithOwner`'s reuse path or from the signin payload (where drafts
 * ARE included so the JWT bid resolves) does not appear as a selectable
 * tenant. See playbook/engineering/incidents/2026-05-24-duplicate-businesses/incident.md.
 */
export const selectBusinesses = createSelector([selectAll], businesses =>
  businesses.filter(b => !!b.setupCompletedAt)
)
export const selectBusinessById = selectById
export const selectBusinessEntities = selectEntities

/**
 * Returns the business matching the JWT's businessId, or undefined if the
 * entity list hasn't loaded yet, the user is in pending-business state, or
 * the JWT references a business the user no longer has access to (deleted /
 * removed in another session). NEVER falls back to "first business in list" —
 * doing so silently mis-routes every business-scoped API call, since they all
 * key off the JWT, not the Redux entity. When you see undefined here, the
 * recovery path is `findMyBusinesses` (refresh list) then a fresh
 * `selectBusinessThunk` or sign-out, not a guess.
 */
export const selectCurrentBusiness = createSelector(
  [selectEntities, selectBusinessId],
  (entities, businessId): BusinessEntity | undefined => {
    if (!businessId) return undefined
    return entities[businessId]
  }
)

/**
 * True when the JWT carries a businessId that doesn't exist in the loaded
 * entity list. Used by the UI to recover (refetch list, drop user back to
 * switcher) instead of silently rendering the wrong business.
 */
export const selectCurrentBusinessMissing = createSelector(
  [selectEntities, selectAll, selectBusinessId],
  (entities, allBusinesses, businessId): boolean => {
    if (!businessId) return false
    if (allBusinesses.length === 0) return false
    return !entities[businessId]
  }
)
